import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EncryptionService } from '../core/encryption.service';
import { ClientUserData, StorageData, TokenAndJTI, ProfessionalUserData, AuthAPIResponse, RefreshedToken, TokenEntryModel, LogoEntryModel } from '@shared/models/auth.interface';
import { Router } from '@angular/router';
import { map, tap } from 'rxjs';
import { AppService } from '../core/app.service';
import { StorageError } from '@modules/error/classes';

@Injectable({
  providedIn: 'root'
})
export class SessionService {
  storage: Storage = localStorage;
  
  constructor(
    private http: HttpClient,
    private router: Router, 
    private encryptionService: EncryptionService, 
    private appService: AppService,
  ) {}

  createNewSession(TokenAndJTI: TokenAndJTI, userData: ClientUserData | ProfessionalUserData): string {
    this.createOrUpdateTokenEntry(TokenAndJTI.jti, TokenAndJTI.token, TokenAndJTI.ukRef, TokenAndJTI.jtiPkr);

    const dataKey = `data:${TokenAndJTI.jti}`  // data:JTI => <Encrypted USER DATA>
    const expiry = this.getExpiryTime(TokenAndJTI.token);
    const userDataAsString = JSON.stringify({ ...userData, expiresAt: expiry }); 

    this.createEncryptedStorageMapping(dataKey, userDataAsString);
    return TokenAndJTI.jti;
  }

  updateTokenAndJTIOnStorage(newJTI: string, newToken: string, ukRef: string, jtiPkr: string) {
    this.createOrUpdateTokenEntry(newJTI, newToken, ukRef, jtiPkr);
    //this.calculateTokenTimeout(newToken);
  }

  getJTIFromCurrentRoute() {
    let fullUrl = this.router.url
    let jti: string = fullUrl.split('/')[2];// get current jti from route.
    return jti;
  }

  getToken(jti: string): string {
    if(jti === undefined || jti === "") {
      return "";
    }

    const key = `sid:${this.encodeBase64(jti)}`;
    const tempKey = `sid:${this.encodeBase64(jti + ':temp')}`;

    let tokenEntry: TokenEntryModel = { token: "", expiresAt: new Date(0), ukRef: "", jtiPkr: "" };
    if(this.storage.getItem(key)) {
      let encData: string = this.encryptionService.decryptData(this.storage.getItem(key) || '');
      tokenEntry = JSON.parse(encData);
      return tokenEntry.token;
    }

    if(this.storage.getItem(tempKey)) {
      let encData: string = this.storage.getItem(tempKey) || '';
      tokenEntry = JSON.parse(encData);
      return tokenEntry.token;
    }

    return tokenEntry.token;
  }

  getUkRef(jti: string): string {
    if(jti === undefined || jti === "") {
      return "";
    }

    const key = `sid:${this.encodeBase64(jti)}`;
    const tempKey = `sid:${this.encodeBase64(jti + ':temp')}`;

    let tokenEntry: TokenEntryModel = { token: "", expiresAt: new Date(0), ukRef: "", jtiPkr: "" };
    if(this.storage.getItem(key)) {
      let encData: string = this.encryptionService.decryptData(this.storage.getItem(key) || '');
      tokenEntry = JSON.parse(encData);
      return tokenEntry.ukRef;
    }

    if(this.storage.getItem(tempKey)) {
      let encData: string = this.storage.getItem(tempKey) || '';
      tokenEntry = JSON.parse(encData);
      return tokenEntry.ukRef;
    }

    return tokenEntry.ukRef;
  }

  getJtiPkr(jti: string): string {
    if(jti === undefined || jti === "") {
      return "";
    }

    const key = `sid:${this.encodeBase64(jti)}`;
    const tempKey = `sid:${this.encodeBase64(jti + ':temp')}`;

    let tokenEntry: TokenEntryModel = { token: "", expiresAt: new Date(0), ukRef: "", jtiPkr: "" };
    if(this.storage.getItem(key)) {
      let encData: string = this.encryptionService.decryptData(this.storage.getItem(key) || '');
      tokenEntry = JSON.parse(encData);
      return this.decodeBase64(tokenEntry.jtiPkr);
    }

    if(this.storage.getItem(tempKey)) {
      let encData: string = this.storage.getItem(tempKey) || '';
      tokenEntry = JSON.parse(encData);
      return this.decodeBase64(tokenEntry.jtiPkr);
    }

    return tokenEntry.jtiPkr;
  }

  getData(jti: string): string | null {
    return this.storage.getItem(`data:${jti}`);
  }

  setLogo(jti: string, logo: string) {
    try {
      this.storage.setItem(`logo:${jti}`, logo);
    } catch (error) {
      console.debug(error);
      throw new StorageError('STORAGE DATA CANNOT BE SAVED', "The logo size of this client’s storage data exceeds the allowed limit. Please contact agency staff to upload the logo again.");
    }
  }

  getLogo() {
    const jti = this.getJTIFromCurrentRoute();
    const logoEntry = this.storage.getItem(`logo:${jti}`);
    if (logoEntry)
      return (JSON.parse(logoEntry) as LogoEntryModel).logo;
    else
      return null;
  }

  createEncryptedStorageMapping(mappingKey: string, data: string) {
    const encryptedToken = this.encryptionService.encryptData(data);
    try {
      this.storage.setItem(mappingKey, encryptedToken);
    } catch (error) {
      console.debug(error);
      throw new StorageError('STORAGE DATA CANNOT BE SAVED', ('localstorage'));
    }
  }

  getDecryptedStorageValue(mappingKey: string): string {
    const encryptedData = this.storage.getItem(mappingKey);

    if (encryptedData) {
      const decryptedData = this.encryptionService.decryptData(encryptedData);
      return decryptedData;
    }

    return '';
  }

  getExpiryTime(token: string): Date {
    const jwtBase64 = token.split('.')[1];
    const jwtToken = JSON.parse(this.decodeBase64(jwtBase64));
    const tokenExpiry = jwtToken.exp * 1000;
    let date: Date = new Date(tokenExpiry);
    // date.setDate(jwtToken.exp * 1000);

    return date;
  }

  createOrUpdateTokenEntry(jti: string, token: string, ukRef: string, jtiPkr: string) {
    const tokenExpiry = this.getExpiryTime(token);
    let tokenEntry: TokenEntryModel = {
      token: token,
      expiresAt: tokenExpiry,
      ukRef: ukRef,
      jtiPkr: this.encodeBase64(jtiPkr)
    };

    const tokenKey = `sid:${this.encodeBase64(jti)}`;
    const tokenTempKey = `sid:${this.encodeBase64(jti + ':temp')}`;
    const encryptedTokenData = this.encryptionService.encryptData(JSON.stringify(tokenEntry));

    try {
      if(!this.storage.getItem(tokenKey)) {
        this.storage.setItem(tokenKey, encryptedTokenData);
      }
      else {
        this.storage.setItem(tokenTempKey, encryptedTokenData);
        this.storage.removeItem(tokenKey);
        this.storage.setItem(tokenKey, encryptedTokenData);
        this.storage.removeItem(tokenTempKey);
      }
    } catch (error) {
      throw new StorageError('UNABLE TO SET TOKEN OR JTI', ('localstorage'));
    }

    this.removeExpiredEntries(this.storage);
  }

  updateRefreshToken(currentJTI: string, newRefreshToken: string, newToken: string) {
    const newData = JSON.parse(this.getDecryptedStorageValue(`data:${currentJTI}`)) as StorageData<ClientUserData | ProfessionalUserData>;
    newData.refreshToken = newRefreshToken;
    newData.expiresAt = this.getExpiryTime(newToken);
    this.createEncryptedStorageMapping(`data:${currentJTI}`, JSON.stringify(newData))
  }

  updateLogoExpiry(currentJTI: string, newToken: string) {
    const existingLogoEntry = this.storage.getItem(`logo:${currentJTI}`);
    if (existingLogoEntry) {
      const newLogoData = JSON.parse(existingLogoEntry) as LogoEntryModel;
      newLogoData.expiresAt = this.getExpiryTime(newToken);
      this.setLogo(currentJTI, JSON.stringify(newLogoData));
    }
  }

  updateTokenAndRefreshTokenOnStorage(newToken: string, newRefreshToken: string, ukRef: string, jtiPkr: string) {
    const currentJTI = this.getJTIFromCurrentRoute();
    this.createOrUpdateTokenEntry(currentJTI, newToken, ukRef, jtiPkr);
    this.updateRefreshToken(currentJTI, newRefreshToken, newToken);
    this.updateLogoExpiry(currentJTI, newToken);

    this.calculateTokenTimeout(newToken);
  }

  private encodeBase64 = (data: any) => {
    return Buffer.from(data).toString('base64');
  }
  private decodeBase64 = (data: any) => {
      return Buffer.from(data, 'base64').toString('ascii');
  }

  calculateTokenTimeout(token: string) {
    const jwtBase64 = token.split('.')[1];
    const jwtToken = JSON.parse(this.decodeBase64(jwtBase64));

    const expires = new Date(jwtToken.exp * 1000);
    const timeout = expires.getTime() - Date.now() - (300 * 1000);
    setTimeout(() => this.getAndSetRefreshToken(token), timeout);
  }

  getRefreshToken(jti: string) {
    let storageData = undefined;
    const encryptedDataString = this.storage.getItem(`data:${jti}`);
    if (encryptedDataString) {
      const decryptedData: string = this.encryptionService.decryptData(encryptedDataString);
      storageData = JSON.parse(decryptedData);
    }
    return storageData?.refreshToken;
  }

  getAndSetRefreshToken(token: string) {
    const jti = this.getJTIFromCurrentRoute() || '';
    const refreshToken = this.getRefreshToken(jti);
    
    if(jti != '') {
      const authAPIDomain = this.appService.getAppConfigs()["AUTH"].BaseUrl;
      const refreshTokenAPI = this.appService.getAppConfigs()["AUTH"].Versions.V1["CLIENTPORTALREFRESHTOKEN"]["RefreshToken"].PATH;
      const url = authAPIDomain + refreshTokenAPI;

      this.http.post<AuthAPIResponse<RefreshedToken>>(url, { token, refreshToken }).pipe(
        map(refreshTokenResponse => refreshTokenResponse.data),
        tap(token => {
          let refreshToken = token.refreshToken;
          let tokenProvided = token.token;
          let ukRef=token.ukRef;
          let jtiPkr=token.jtiPkr;
          this.updateTokenAndRefreshTokenOnStorage(tokenProvided, refreshToken,ukRef,jtiPkr);
        })
      ).subscribe();
    }
  }

  isNewTokenRequired(token: string) {
    const jwtBase64 = token.split('.')[1];
    const jwtToken = JSON.parse(this.decodeBase64(jwtBase64));

    const expires = new Date(jwtToken.exp * 1000);
    const timeout = expires.getTime() - Date.now() - (60 * 1000);
    const minTime = 60 * 1000;

    return (timeout <= minTime);
  }

  removeMappingFor(jti: string) {
    // storage.removeItem(`sid:${jti}`);  moved to localstorage.
    this.storage.removeItem(`data:${jti}`);
    this.storage.removeItem(`logo:${jti}`);
    this.removeTokenCookie(jti);
  }

  removeTokenCookie(jti: string) {
    const key = `sid:${this.encodeBase64(jti)}`;
    const tempKey = `sid:${this.encodeBase64(jti + ':temp')}`;

    if(this.storage.getItem(key)) {
      this.storage.removeItem(key);
    }

    if(this.storage.getItem(tempKey)) {
      this.storage.removeItem(tempKey);
    }

    this.removeExpiredEntries(this.storage);
  }

  removeExpiredEntries(storage: Storage) {
    const currentTime: Date = new Date();
    let decryptedStorageValueAsString: string = '';

    let entryType: 'TOKEN' | 'DATA' | 'LOGO' | 'NONE' = 'NONE';

    for (let [key, value] of Object.entries(storage)) {
      if (key.slice(0,3) === 'sid') entryType = 'TOKEN';
      else if (key.slice(0,4) === 'data') entryType = 'DATA';
      else if (key.slice(0,4) === 'logo') entryType = 'LOGO';
      else entryType = 'NONE';

      try {
        // let encData: string = this.encryptionService.decryptData(storage.getItem(key) || '');
        // let cookieData: TokenCookie = { token: "", expiresAt: 0 };
        // cookieData = JSON.parse(encData);


        switch(entryType) {
          case 'TOKEN':
            decryptedStorageValueAsString = this.encryptionService.decryptData(value || '');
            let tokenData: TokenEntryModel = JSON.parse(decryptedStorageValueAsString);
            const tokenExpiryTime = new Date(tokenData.expiresAt);
            const tokenExpired = currentTime > tokenExpiryTime;

            if(tokenExpired) storage.removeItem(key);
            break;

          case 'DATA':
            decryptedStorageValueAsString = this.encryptionService.decryptData(value || '');
            let userData: StorageData<any> = JSON.parse(decryptedStorageValueAsString);
            const userDataExpiryTime = new Date (userData.expiresAt);
            const userDataExpired = currentTime > userDataExpiryTime;

            if(userDataExpired) storage.removeItem(key);
            break;

          case 'LOGO':
            decryptedStorageValueAsString = value || '';
            let logoData: LogoEntryModel = JSON.parse(decryptedStorageValueAsString);
            const logoDataExpiryTime = new Date(logoData.expiresAt);
            const logoDataExpired = currentTime > logoDataExpiryTime;

            if(logoDataExpired) storage.removeItem(key);
            break;

          case 'NONE':
          default:
            console.debug("No entry Matched");
            break;
        }

      }
      catch (error) {
        console.debug(error);
        throw new StorageError('STORAGE DATA CANNOT BE SAVED', ('localstorage'));
      }
    }
  }

  createNonEncryptedStorageMapping(key: string, value: any) {
    this.storage.setItem(key, value);
  }

  getNonEncryptedStorageMapping(key: string) {
   return this.storage.getItem(key);
  }

  public getTimeStampValue(): string {
    const currentDate = new Date(); // Format to UTC string
      //const addMinutes =this.appService.getAppSettings().uqReqTm;
      let addMinutes = this.appService.getAppSettings().uqReqTm;
 
     // Validate addMinutes and provide fallback
     if (addMinutes === undefined || addMinutes === null || isNaN(addMinutes)) {
       console.warn('⚠️ uqReqTm is invalid or undefined, defaulting to 1 minute');
       addMinutes = 1; // Default to 1 minute
	     }
      const futureDate = new Date(currentDate);
      futureDate.setMinutes(currentDate.getMinutes() + addMinutes);      
      const futureDateISOString = futureDate.toISOString(); // Convert to ISO string
   return futureDateISOString;
  }
}
